import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Ban, CheckCheck, Info, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import { useAcknowledgeAllFlags, useAcknowledgeFlag } from '../../features/flags/mutations';
import { useFlags } from '../../features/flags/queries';
import type { BehaviouralFlagDto } from '../../lib/api/endpoints';
import { relativeTimestamp } from '../../lib/format/date';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

function severityIcon(flag: BehaviouralFlagDto) {
  if (flag.severity === 'BLOCK') return <Ban className="h-4 w-4 text-danger" aria-hidden />;
  if (flag.severity === 'WARN') {
    return <AlertTriangle className="h-4 w-4 text-warn" aria-hidden />;
  }
  return <Info className="h-4 w-4 text-primary" aria-hidden />;
}

function flagTitle(flag: BehaviouralFlagDto): string {
  return flag.kind
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export function BehaviouralFlagFeed() {
  const flags = useFlags({ unack: true });
  const acknowledge = useAcknowledgeFlag();
  const acknowledgeAll = useAcknowledgeAllFlags();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const rows = flags.data ?? [];
  const pending = acknowledge.isPending || acknowledgeAll.isPending;

  function confirmAcknowledgeAll() {
    acknowledgeAll.mutate(undefined, {
      onSuccess: () => setConfirmOpen(false),
    });
  }

  return (
    <Card
      header={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Alerts</h2>
            <p className="text-sm text-text-muted">
              Unacknowledged behavioural discipline tripwires.
            </p>
          </div>
          <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
            <Dialog.Trigger asChild>
              <Button
                aria-label="Acknowledge all behavioural flags"
                disabled={flags.isLoading || rows.length === 0 || pending}
                size="sm"
                type="button"
                variant="secondary"
              >
                <CheckCheck className="mr-2 h-4 w-4" aria-hidden />
                Acknowledge all
              </Button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60" />
              <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded border border-border bg-surface p-5 text-text shadow-xl">
                <Dialog.Title className="text-lg font-semibold">
                  Acknowledge all flags?
                </Dialog.Title>
                <Dialog.Description className="mt-2 text-sm text-text-muted">
                  This will acknowledge {rows.length} unacknowledged behavioural{' '}
                  {rows.length === 1 ? 'flag' : 'flags'}.
                </Dialog.Description>
                <div className="mt-5 flex justify-end gap-3">
                  <Dialog.Close asChild>
                    <Button type="button" variant="secondary">
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button
                    aria-label="Confirm acknowledge all behavioural flags"
                    disabled={acknowledgeAll.isPending}
                    onClick={confirmAcknowledgeAll}
                    type="button"
                  >
                    Confirm
                  </Button>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      }
    >
      {flags.isLoading ? (
        <p className="text-sm text-text-muted">Loading flags...</p>
      ) : rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((flag) => (
            <div
              key={flag.id}
              className="flex flex-col gap-3 rounded border border-border bg-bg-muted p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 gap-3">
                <div className="mt-0.5 shrink-0">{severityIcon(flag)}</div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{flagTitle(flag)}</span>
                    <span className="rounded bg-bg px-2 py-0.5 text-xs font-medium">
                      {flag.severity}
                    </span>
                    {flag.subjectSymbol ? (
                      <span className="rounded bg-bg px-2 py-0.5 text-xs font-medium">
                        {flag.subjectSymbol}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-text-muted">
                    Raised {relativeTimestamp(flag.createdAt)}
                  </p>
                </div>
              </div>
              <Button
                aria-label={`Acknowledge ${flag.kind}`}
                disabled={pending}
                onClick={() => acknowledge.mutate(flag.id)}
                size="sm"
                type="button"
                variant="secondary"
              >
                Ack
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-text-muted">
          <ShieldCheck className="h-4 w-4 text-success" aria-hidden />
          No active behavioural flags.
        </div>
      )}
    </Card>
  );
}
